import { QuantityBreaksUpdatedEvent } from '@theme/events';

class VariantQuantityPicker extends HTMLElement {
  connectedCallback() {
    this.handleResize = () => this.#syncProgress();

    this.#bindItems();
    this.#syncUi();

    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
  }

  isActive() {
    return true;
  }

  isSelectionComplete() {
    return this.getSelectedItems().length > 0;
  }

  getSelectedItems() {
    return this.#getItems()
      .map((item) => ({
        id: item.dataset.variantId,
        quantity: parseInt(item.dataset.quantity, 10) || 0,
      }))
      .filter((item) => item.id && item.quantity > 0);
  }

  getSelectedQuantity() {
    return this.getSelectedItems().reduce((total, item) => total + item.quantity, 0);
  }

  getSelectedSubtotalPrice() {
    return this.#getItems().reduce((total, item) => {
      const quantity = parseInt(item.dataset.quantity, 10) || 0;
      const price = parseInt(item.dataset.price, 10) || 0;

      return total + price * quantity;
    }, 0);
  }

  getSelectedSubtotalCompareAtPrice() {
    return this.#getItems().reduce((total, item) => {
      const quantity = parseInt(item.dataset.quantity, 10) || 0;
      const compareAtPrice = parseInt(item.dataset.compareAtPrice, 10) || parseInt(item.dataset.price, 10) || 0;

      return total + compareAtPrice * quantity;
    }, 0);
  }

  getSelectedTotalPrice() {
    const subtotalPrice = this.getSelectedSubtotalPrice();
    const completedStep = this.#getCompletedStep(this.#getSteps(), this.getSelectedQuantity(), subtotalPrice);

    return this.#applyStepDiscount(subtotalPrice, completedStep);
  }

  getSelectedTotalCompareAtPrice() {
    const subtotalPrice = this.getSelectedSubtotalPrice();
    const subtotalCompareAtPrice = this.getSelectedSubtotalCompareAtPrice();
    const completedStep = this.#getCompletedStep(this.#getSteps(), this.getSelectedQuantity(), subtotalPrice);

    if (!completedStep || completedStep.dataset.discountType === 'none') {
      return subtotalCompareAtPrice;
    }

    return Math.max(subtotalCompareAtPrice, subtotalPrice);
  }

  #getItems() {
    return Array.from(this.querySelectorAll('[data-ref="variant-quantity-picker-item"]'));
  }

  #getSteps() {
    return Array.from(this.querySelectorAll('[data-ref="variant-quantity-picker-step"]'));
  }

  #getStepTarget(step) {
    const type = step.dataset.targetType === 'amount' ? 'amount' : 'quantity';
    const quantityTarget = parseInt(step.dataset.targetQuantity, 10) || 0;
    const amountTarget = parseInt(step.dataset.targetAmount, 10) || 0;

    return {
      type,
      value: type === 'amount' ? amountTarget : quantityTarget,
    };
  }

  #getMetricValue(type, selectedQuantity, selectedAmount) {
    return type === 'amount' ? selectedAmount : selectedQuantity;
  }

  #getStepDiscount(step) {
    if (!step) {
      return {
        type: 'none',
        value: 0,
        percentage: 0,
      };
    }

    return {
      type: step.dataset.discountType || 'none',
      value: parseInt(step.dataset.discountValue, 10) || 0,
      percentage: parseInt(step.dataset.discountPercentage, 10) || 0,
    };
  }

  #applyStepDiscount(basePrice, step) {
    const discount = this.#getStepDiscount(step);

    if (discount.type === 'value') {
      return Math.max(0, basePrice - discount.value);
    }

    if (discount.type === 'percentage') {
      return Math.max(0, basePrice - Math.round((basePrice * discount.percentage) / 100));
    }

    return basePrice;
  }

  #isStepComplete(step, selectedQuantity, selectedAmount) {
    const target = this.#getStepTarget(step);
    if (target.value <= 0) return false;

    return this.#getMetricValue(target.type, selectedQuantity, selectedAmount) >= target.value;
  }

  #getProgressPercent(steps, selectedQuantity, selectedAmount) {
    if (steps.length === 0) return 0;

    const stepTargets = steps.map((step) => this.#getStepTarget(step));

    if (steps.length === 1) {
      const target = stepTargets[0];
      if (target.value <= 0) return 0;

      const value = this.#getMetricValue(target.type, selectedQuantity, selectedAmount);
      return value >= target.value ? 100 : 0;
    }

    let completedIndex = -1;

    for (const [index, step] of steps.entries()) {
      if (!this.#isStepComplete(step, selectedQuantity, selectedAmount)) break;
      completedIndex = index;
    }

    if (completedIndex < 0) return 0;

    if (completedIndex >= steps.length - 1) return 100;

    const nextIndex = completedIndex + 1;
    const nextTarget = stepTargets[nextIndex];
    const nextValue = this.#getMetricValue(nextTarget.type, selectedQuantity, selectedAmount);

    let segmentRatio = nextTarget.value > 0 ? nextValue / nextTarget.value : 0;

    if (completedIndex >= 0) {
      const previousTarget = stepTargets[completedIndex];

      if (previousTarget.type === nextTarget.type) {
        const denominator = nextTarget.value - previousTarget.value;
        segmentRatio = denominator > 0 ? (nextValue - previousTarget.value) / denominator : 1;
      }
    }

    segmentRatio = Math.max(0, Math.min(segmentRatio, 1));

    const segmentWidth = 100 / (steps.length - 1);
    const basePercent = completedIndex < 0 ? 0 : completedIndex * segmentWidth;

    return basePercent + segmentRatio * segmentWidth;
  }

  #getCompletedStep(steps, selectedQuantity, selectedAmount) {
    let completedStep = null;

    for (const step of steps) {
      if (!this.#isStepComplete(step, selectedQuantity, selectedAmount)) break;
      completedStep = step;
    }

    return completedStep;
  }

  #bindItems() {
    for (const item of this.#getItems()) {
      const minusButton = item.querySelector('[data-ref="variant-quantity-picker-minus"]');
      const plusButton = item.querySelector('[data-ref="variant-quantity-picker-plus"]');
      const input = item.querySelector('[data-ref="variant-quantity-picker-input"]');

      minusButton?.addEventListener('click', (event) => {
        event.preventDefault();
        const current = parseInt(input?.value, 10) || 0;
        this.#setItemQuantity(item, current - 1);
      });

      plusButton?.addEventListener('click', (event) => {
        event.preventDefault();
        const current = parseInt(input?.value, 10) || 0;
        this.#setItemQuantity(item, current + 1);
      });

      input?.addEventListener('change', () => {
        const current = parseInt(input.value, 10) || 0;
        this.#setItemQuantity(item, current);
      });
    }
  }

  #setItemQuantity(item, quantity) {
    const input = item.querySelector('[data-ref="variant-quantity-picker-input"]');
    if (!input) return;

    const nextQuantity = Math.max(0, quantity);
    input.value = String(nextQuantity);
    item.dataset.quantity = String(nextQuantity);

    this.#syncItemUi(item, nextQuantity);
    this.#syncProgress();
    document.dispatchEvent(new QuantityBreaksUpdatedEvent());
  }

  #syncUi() {
    for (const item of this.#getItems()) {
      const quantity = parseInt(item.dataset.quantity, 10) || 0;
      this.#syncItemUi(item, quantity);
    }

    this.#syncProgress();
  }

  #syncItemUi(item, quantity) {
    item.classList.toggle('is-selected', quantity > 0);
  }

  #syncProgress() {
    const progress = this.querySelector('[data-ref="variant-quantity-picker-progress"]');
    const meter = this.querySelector('.variant-quantity-picker__progress-meter');
    const track = this.querySelector('.variant-quantity-picker__progress-track');
    const fill = this.querySelector('[data-ref="variant-quantity-picker-progress-fill"]');
    const steps = this.#getSteps();

    if (!progress || !meter || !track || !fill) return;

    if (steps.length === 0) {
      progress.hidden = true;
      return;
    }

    progress.hidden = false;

    const markers = steps
      .map((step) => step.querySelector('.variant-quantity-picker__progress-step-marker'))
      .filter(Boolean);

    if (markers.length === 0) return;

    const meterRect = meter.getBoundingClientRect();
    const firstMarkerRect = markers[0].getBoundingClientRect();
    const lastMarkerRect = markers[markers.length - 1].getBoundingClientRect();
    const lineOffset = Math.min(firstMarkerRect.width * 0.35, 12);

    const lineLeft = Math.max(0, firstMarkerRect.left + firstMarkerRect.width / 2 - meterRect.left - lineOffset);
    const lineRight = Math.min(meterRect.width, lastMarkerRect.left + lastMarkerRect.width / 2 - meterRect.left + lineOffset);
    const lineWidth = Math.max(0, lineRight - lineLeft);
    const lineTop = firstMarkerRect.top + firstMarkerRect.height / 2 - meterRect.top;

    const selectedQuantity = this.getSelectedQuantity();
    const selectedAmount = this.getSelectedSubtotalPrice();
    const percent = this.#getProgressPercent(steps, selectedQuantity, selectedAmount);
    const hasSelection = selectedQuantity > 0 || selectedAmount > 0;

    track.style.left = `${lineLeft}px`;
    track.style.top = `${lineTop}px`;
    track.style.width = `${lineWidth}px`;

    fill.style.left = `${lineLeft}px`;
    fill.style.top = `${lineTop}px`;
    fill.style.width = `${(lineWidth * percent) / 100}px`;

    let completedIndex = -1;

    for (const [index, step] of steps.entries()) {
      if (!this.#isStepComplete(step, selectedQuantity, selectedAmount)) break;
      completedIndex = index;
    }

    const activeIndex =
      completedIndex >= steps.length - 1 ? steps.length - 1 : Math.max(0, completedIndex + (hasSelection ? 1 : 0));

    const activeStep = steps[activeIndex] || null;
    fill.style.background = '';

    for (const step of steps) {
      step.classList.remove('is-active');
      step.classList.toggle('is-complete', this.#isStepComplete(step, selectedQuantity, selectedAmount));
    }

    if (completedIndex >= 0 && hasSelection && activeStep && !activeStep.classList.contains('is-complete')) {
      activeStep.classList.add('is-active');
    }

  }
}

if (!customElements.get('variant-quantity-picker')) {
  customElements.define('variant-quantity-picker', VariantQuantityPicker);
}
